/**
 * Nox: Führt Artikelanzahl und Summe im Warenkorb-Button nach.
 *
 * Horizons cart-icon.js aktualisiert nur die Zahlen-Bubble. Der Entwurf zeigt
 * im Button zusätzlich "3 Artikel · 21,17 €", deshalb hier ein additiver
 * Listener auf dasselbe Event. Horizon-Dateien bleiben unangetastet.
 */

import { StandardEvents } from '@shopify/events';

const COUNT_SELECTOR = '[data-nox-cart-count]';
const TOTAL_SELECTOR = '[data-nox-cart-total]';

/** Vorlage aus dem Locale, z. B. "{{ count }} Artikel" */
function itemsLabel(template, count) {
  return template.replace(/\{\{\s*count\s*\}\}/g, String(count));
}

/**
 * Bildet Shopifys money_format nach, damit Server und Client identisch
 * formatieren. Ohne das stünde nach dem Nachladen ein anderes Format da als
 * beim ersten Rendern.
 *
 * @param {number} cents - Betrag in der kleinsten Währungseinheit.
 * @param {string} [format] - z. B. "€{{amount}}" aus shop.money_format.
 */
function formatMoney(cents, format) {
  const value = cents / 100;

  const withDot = (decimals) =>
    value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  const withComma = (decimals) =>
    value.toLocaleString('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  if (!format) return withComma(2);

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_match, name) => {
    switch (name) {
      case 'amount':
        return withDot(2);
      case 'amount_no_decimals':
        return withDot(0);
      case 'amount_with_comma_separator':
        return withComma(2);
      case 'amount_no_decimals_with_comma_separator':
        return withComma(0);
      case 'amount_with_apostrophe_separator':
        return withDot(2).replace(/,/g, "'");
      default:
        return withComma(2);
    }
  });
}

async function refresh() {
  const countEl = document.querySelector(COUNT_SELECTOR);
  const totalEl = document.querySelector(TOTAL_SELECTOR);
  if (!countEl && !totalEl) return;

  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const cart = await response.json();

    if (countEl) {
      // Die Vorlage steckt im initial gerenderten Text; beim ersten Lauf merken.
      if (!countEl.dataset.noxTemplate) {
        const initial = countEl.textContent.trim();
        countEl.dataset.noxTemplate = initial.replace(/^\d+/, '{{ count }}');
      }
      countEl.textContent = itemsLabel(countEl.dataset.noxTemplate, cart.item_count);
    }

    if (totalEl && typeof cart.total_price === 'number') {
      totalEl.textContent = formatMoney(cart.total_price, totalEl.dataset.noxMoneyFormat);
    }
  } catch (error) {
    // Stillschweigend: der serverseitig gerenderte Stand bleibt stehen.
    console.warn('[nox-cart-summary]', error);
  }
}

// Dasselbe Event, auf das auch Horizons cart-icon.js hört.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
  if (promise) {
    promise.then(refresh).catch(() => refresh());
  } else {
    refresh();
  }
});
